/*
********************************************************************************
Supplemental functions related to PDF generation using jsPDF and jsPDF-AutoTable 
libraries.  Moved to separate file to declutter main function that actually 
generates the output PDF.
********************************************************************************
*/

//@ts-nocheck
// Toggle preceding from @ts-nocheck to @ts-check to enable TypeScript checking

import { consoleMsg } from "./utility.js";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import autoTable from "jspdf-autotable";

// prettier-ignore
export {
   addDataTable,
   addDetailsTable,
   addNotesTable,
   formatTable,
   addCarImagesTable,
   resizeImageForPdf,
   calculateOffset,
   addFooter,
   savePDF,
   downloadPDF
};

// Make sure our app references are using the global app object associated with window
const app = window.app;

const fillColor = 168;

// Function to retrieve form data from Ionic storage
async function getPages(...pageKeys) {
	try {
		// Use Promise.all to retrieve all stored objects concurrently
		const pages = await Promise.all(pageKeys.map((key) => store.get(key)));
		return pages;
	} catch (error) {
		console.error("Error retrieving pages from storage: ", error);
		return [];
	}
}

function addCarImagesTable(doc, cars, margin, yPosition, pageWidth) {
	const cols = 4;
	const cellSpacing = 2;
	const cellWidth = (pageWidth - 2 * margin) / cols;

	// Filter valid images
	const validImages = cars.filter((img) => img && img.photo && img.id);

	if (validImages.length === 0) {
		console.warn("No valid images found for the table.");
		return yPosition;
	}

	const tableBody = [];
	for (let i = 0; i < validImages.length; i += cols) {
		const idRow = [];
		const imageRow = [];

		for (let c = 0; c < cols; c++) {
			const imgIndex = i + c;
			if (imgIndex < validImages.length) {
				const image = validImages[imgIndex];
				const aspectRatio = image.ratio || 0.75;
				const cellHeight = cellWidth * aspectRatio;

				idRow.push({ content: `${image.label}`, colSpan: 1, styles: { halign: "center", cellWidth: cellWidth, fillColor: [240, 240, 240] } });
				imageRow.push({ content: "", styles: { minCellHeight: cellHeight } });
			} else {
				idRow.push("");
				imageRow.push("");
			}
		}

		tableBody.push(idRow);
		tableBody.push(imageRow);
	}

	autoTable(doc, {
		body: tableBody,
		theme: "grid",
		startY: yPosition,
		tableWidth: "auto",
		styles: { fontSize: 8, valign: "middle" },
		margin: { left: margin, right: margin },
		didDrawCell: function (data) {
			const imgIndex = Math.floor(data.row.index / 2) * cols + data.column.index;
			if (data.row.index % 2 === 1 && imgIndex < validImages.length) {
				const image = validImages[imgIndex];
				const xPos = data.cell.x + cellSpacing / 2;
				const yPos = data.cell.y + cellSpacing / 2;
				const imgW = cellWidth - cellSpacing;
				const imgH = imgW * image.ratio;
				doc.addImage(image.photo, "JPEG", xPos, yPos, imgW, imgH);
			}
		},
	});

	return doc.lastAutoTable.finalY + 5;
}

function resizeImageForPdf(dataUrl, maxWidthPx, quality = 0.85) {
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			const scale = Math.min(1, maxWidthPx / img.naturalWidth);
			const canvas = document.createElement("canvas");
			canvas.width = Math.round(img.naturalWidth * scale);
			canvas.height = Math.round(img.naturalHeight * scale);
			canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
			resolve(canvas.toDataURL("image/jpeg", quality));
		};
		img.onerror = () => {
			console.warn("resizeImageForPdf: image failed to load, using original");
			resolve(dataUrl);
		};
		img.src = dataUrl.startsWith("data:") ? dataUrl : `data:image/jpeg;base64,${dataUrl}`;
	});
}

function addDetailsTable(doc, data, margin, yPosition, contCb = null) {
	const body = [];
	const dataKeys = Object.keys(data);
	const dataValues = Object.values(data);
	// Define the headers and data
	const headers = [];
	const values = [];
	for (let i = 0; i < dataKeys.length; i++) {
		headers.push(dataKeys[i]);
		values.push(dataValues[i]);
	}
	//consoleMsg("Data keys length: ", dataKeys.length);
	for (let i = 0; i < headers.length; i += 2) {
		body.push([dataKeys[i], dataValues[i], dataKeys[i + 1], dataValues[i + 1]]);
	}
	formatTable(doc, body, margin, yPosition, null, contCb);
}

function addNotesTable(doc, notesContent, margin, yPosition, contCb = null) {
	// Create a single-cell table with the notes content
	const body = [[notesContent]];

	autoTable(doc, {
		body: body,
		theme: "grid",
		startY: yPosition,
		tableWidth: "auto",
		styles: {
			fontSize: 8,
			//valign: "middle",
			//cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
			//minCellHeight: 6,
			fillColor: [255, 255, 255], // Force white background
		},
		margin: contCb ? { top: margin + 8, left: margin, right: margin } : { left: margin, right: margin },
		...(contCb && { didDrawPage: contCb }),
	});

	return doc.lastAutoTable.finalY;
}

function formatTable(doc, body, margin, yPosition, colStyles = null, contCb = null) {
	const pageWidth = Math.round(doc.internal.pageSize.width);
	const availWidth = pageWidth - 2 * margin;

	let columnStyles;
	if (colStyles) {
		columnStyles = colStyles;
	} else {
		// Detect logical column count from the widest row (respecting colSpan)
		const numCols = body.length > 0 ? Math.max(...body.map((row) => row.reduce((sum, cell) => sum + ((cell && cell.colSpan) || 1), 0))) : 2;

		if (numCols >= 4) {
			const labelW = 38;
			const valueW = (availWidth - 2 * labelW) / 2;
			columnStyles = {
				0: { cellWidth: labelW },
				1: { cellWidth: valueW },
				2: { cellWidth: labelW },
				3: { cellWidth: valueW },
			};
		} else {
			const questionW = 120;
			columnStyles = {
				0: { cellWidth: questionW },
				1: { cellWidth: availWidth - questionW },
			};
		}
	}

	autoTable(doc, {
		body: body,
		theme: "grid",
		startY: yPosition,
		tableWidth: "auto",
		styles: {
			fontSize: 8,
			valign: "middle",
			cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 },
			minCellHeight: 5.5,
		},
		columnStyles: columnStyles,
		margin: contCb ? { top: margin + 8, left: margin, right: margin } : { left: margin, right: margin },
		...(contCb && { didDrawPage: contCb }),
		didParseCell: function (data) {
			if (data.section === "body") {
				if (data.cell.raw && data.cell.raw.styles && data.cell.raw.styles.fillColor) {
					data.cell.styles.fillColor = data.cell.raw.styles.fillColor;
				} else if (data.column.index % 2 === 0) {
					data.cell.styles.fillColor = [240, 240, 240]; // Light gray for even columns
				} else {
					data.cell.styles.fillColor = [255, 255, 255]; // White for odd columns
				}
			}
		},
	});
}

function setPageTitle(page) {
	let string = "";
	switch (page) {
		case "2":
			string = "Exterior Inspection";
			break;
		case "3":
			string = "Glovebox Documents";
			break;
		case "4":
			string = "Interior Check";
			break;
		case "5":
			string = "Road Test";
			break;
		case "6":
			string = "Lights Check";
			break;
		case "7":
			string = "Fluid Inspection";
			break;
		case "8":
			string = "Tire Inspection";
			break;
	}
	return string;
}

function addDataTable(doc, data, margin, yPosition, currentPage, contCb = null) {
	const body = [];
	const headers = [];
	const values = [];
	const problems = [];
	const keys = Object.keys(data[currentPage]);
	for (let i = 0; i < keys.length; i++) {
		const row = data[currentPage][i + 1];
		headers.push(row.string);
		values.push(row.value);
		problems.push(row.problemValue);
	}

	let pending = null;

	for (let i = 0; i < headers.length; i++) {
		if (headers[i] === "") {
			// Problem text — flush any pending unpaired Q&A first, then span all 4 columns
			if (pending) {
				body.push([...pending, "", ""]);
				pending = null;
			}
			body.push([{ content: "Problem details: " + values[i], colSpan: 4, styles: { fillColor: [255, 255, 255] } }]);
		} else {
			// Each question declares its own problem answer via data-problem-value, captured
			// at save time. Tint only that question's answer cell — the printed row holds two
			// independent questions.
			const isProblem = values[i] === problems[i];
			const problemStyle = { fillColor: [249, 229, 231], textColor: [164, 10, 30] };
			const qCell = { content: headers[i] };
			const aCell = isProblem ? { content: values[i], styles: problemStyle } : { content: values[i] };

			if (pending) {
				body.push([...pending, qCell, aCell]);
				pending = null;
			} else {
				pending = [qCell, aCell];
			}
		}
	}

	// Flush any remaining unpaired Q&A
	if (pending) {
		body.push([...pending, "", ""]);
	}

	// 80/20 split per pair for Q&A tables (answers are short: OK, Yes, No, etc.)
	const pageWidth = Math.round(doc.internal.pageSize.width);
	const availWidth = pageWidth - 2 * margin;
	const questionW = Math.round(availWidth * 0.4); // 80% of each half-width pair
	const answerW = Math.round(availWidth / 2) - questionW;
	const colStyles = {
		0: { cellWidth: questionW },
		1: { cellWidth: answerW },
		2: { cellWidth: questionW },
		3: { cellWidth: answerW },
	};

	formatTable(doc, body, margin, yPosition, colStyles, contCb);
}

function addTable(doc, data, margin, yPosition, cols = 4) {
	const body = [];
	const dataKeys = Object.keys(data);
	const dataValues = Object.values(data);
	// Define the headers and data
	const headers = [];
	const values = [];
	for (let i = 0; i < dataKeys.length; i++) {
		headers.push(dataKeys[i]);
		values.push(dataValues[i]);
	}
	//consoleMsg("Data keys length: ", dataKeys.length);
	if (cols === 4) {
		for (let i = 0; i < headers.length; i += 2) {
			body.push([dataKeys[i], dataValues[i], dataKeys[i + 1], dataValues[i + 1]]);
		}
	} else {
		for (let i = 0; i < headers.length; i++) {
			consoleMsg("Data keys: ", dataKeys[i]);

			if (dataKeys[i].includes("text") || dataKeys[i] === "") {
			} else {
				body.push([dataKeys[i], dataValues[i]]);
			}
		}
	}

	autoTable(doc, {
		body: body,
		theme: "grid",
		startY: yPosition,
		tableWidth: "auto",
		styles: { fontSize: 8, valign: "middle", minCellWidth: 15 },
		margin: { left: margin, right: margin },
		didParseCell: function (data) {
			if (data.section === "body") {
				// Check to see if the cell has a fillColor explicitly defined (e.g., textarea values)
				if (data.cell.raw && data.cell.raw.styles && data.cell.raw.styles.fillColor) {
					data.cell.styles.fillColor = data.cell.raw.styles.fillColor;
				} else if (data.column.index % 2 === 0) {
					data.cell.styles.fillColor = [240, 240, 240]; // Light gray for even columns
				} else {
					data.cell.styles.fillColor = [255, 255, 255]; // White for odd columns
				}
			}
		},
	});
}

// Define a function to calculate the Y offset
function calculateOffset(doc, lineHeight) {
	let offset = doc.getFontSize() * lineHeight * 0.352778; // 0.352778 is the conversion factor from mm to pixels;
	offset = parseFloat(offset.toFixed(2));
	//consoleMsg("yOffset = ", offset);
	return offset;
}

function addFooter(doc, margin, pageWidth, pageHeight, direction) {
	const totalPages = doc.getNumberOfPages();
	const now = new Date();
	const currentDate = now.toLocaleDateString();
	const currentTime = now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

	for (let i = 1; i <= totalPages; i++) {
		doc.setPage(i);
		doc.setTextColor(169);
		//const footerTitle = "Vehicle Inspection Summary";
		const footerTitle = `${direction} Inspection Summary`;
		const footerPage = `Page ${i} of ${totalPages}`;
		const footerDate = `${currentDate} ${currentTime}`;
		doc.text(footerTitle, margin, pageHeight - 10, null, null, "left");
		doc.text(footerPage, pageWidth / 2, pageHeight - 10, null, null, "center");
		doc.text(footerDate, pageWidth - margin, pageHeight - 10, null, null, "right");
	}
}

async function savePDF(fileName, base64data) {
	let directory;
	const platform = Capacitor.getPlatform();

	if (platform === "ios" || platform === "android") {
		directory = Directory.Documents;
	} else {
		// For web/PWA on Mac/PC, use Downloads
		directory = Directory.Downloads;
	}

	await Filesystem.writeFile({
		path: fileName,
		data: base64data,
		directory: directory,
		encoding: "utf8",
	});

	// Do NOT open the PDF automatically
	// await Browser.open({ url: fileUri.uri });
}

function downloadPDF(fileName, base64data) {
	const link = document.createElement("a");
	link.href = "data:application/pdf;base64," + base64data;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

async function setTitle(titleString) {
	const title = titleString;
	doc.title = title;
}
